/* eslint-disable complexity */
import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import apiFetch from '@scale/frontend-lib/api-fetch';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Option,
  Tooltip,
  TextInput,
  Box,
} from 'design-system';
import CircularProgress from 'design-system/CircularProgress';
import Table from 'corp/components/UI/Table';
import TagSelector from 'corp/components/UI/TagSelector';
import SlackChannelCountries from 'corp/components/SlackChannel/SlackChannelCountries';
import SlackChannelReviewLevels from 'corp/components/SlackChannel/SlackChannelReviewLevels';
import SlackChannelWorkerSources from 'corp/components/SlackChannel/SlackChannelWorkerSources';
import SlackChannelTierStatuses from 'corp/components/SlackChannel/SlackChannelTierStatuses';
import SlackChannelTags from 'corp/components/SlackChannel/SlackChannelTags';
import SlackChannelRequireAllTags from 'corp/components/SlackChannel/SlackChannelRequireAllTags';
import { parseObjectIds, parseEmails, parseSlackIds } from 'corp/lib/parseInputText';
import { SearchOptions } from '@scale/shared/workerSlack/constants';
import Chip from 'react-scale/catalog/components/Chip';
import { twMerge } from 'tailwind-merge';
/* eslint-disable restrict-imports/restrict-imports */
import { IconButton, Select } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertProps, AlertColor } from '@mui/material/Alert';
import { ITagJson } from '@scale/shared/types/tag';
import { ICommunity } from '@scale/shared/types/Community';
import { TextField, SelectMenu, Typography, Checkbox } from '@scale/scaleui';
import useGetCommunities from 'react-scale/hooks/useGetCommunities';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import SAlert from 'react-s-alert';

const TABLE_LIMIT = 18;
interface ISlackInviteTaskerModalProps {
  isOpen: boolean;
  channelName: string;
  channelId: string;
  taskerIds?: string[];
  onInvitePre?: () => void;
  onInvitePost?: (result: any) => void;
  onClose?: () => void;
  isExpert?: boolean;
}

export const SlackInviteTaskerModal: React.FC<ISlackInviteTaskerModalProps> = props => {
  const { isOpen, channelName, channelId, isExpert } = props;
  const { onInvitePre, onInvitePost, onClose } = props;

  const [taskerIdsToInvite, setTaskerIdsToInvite] = useState<string>(
    Array.isArray(props.taskerIds) ? props.taskerIds.join(', ') : props.taskerIds ?? '',
  );

  const inviteTaskers = async () => {
    if (onInvitePre) onInvitePre();
    const result = await apiFetch(
      null,
      `corp-api/scalers/slack_channels/${channelId}/invite?limit=${TABLE_LIMIT}`,
      { method: 'POST', body: { taskerIds: parseObjectIds(taskerIdsToInvite), isExpert } },
    );
    if (onInvitePost) onInvitePost(result);
  };

  return (
    <Dialog open={isOpen}>
      <DialogTitle>Invite Taskers{channelName ? ` to #${channelName}` : ''}</DialogTitle>
      <DialogContent>
        <TextInput
          size="large"
          required
          fullWidth
          placeholder="Tasker IDs"
          label="Tasker IDs"
          helperText="Delimit any way you want"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setTaskerIdsToInvite(e.target.value);
          }}
          value={taskerIdsToInvite}
        />
      </DialogContent>
      <DialogActions>
        <Button
          type="primary"
          disabled={!parseObjectIds(taskerIdsToInvite).length}
          onClick={() => inviteTaskers()}
        >
          Invite
        </Button>
        <Button
          type="error"
          onClick={() => {
            if (onClose) onClose();
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ISlackChannelCreateModalProps {
  isOpen: boolean;
  channelName: string;
  communityOptions: { id: string; name: string; label: string }[];
  onClose?: () => void;
  getMatchingChannels: (channelName: string) => $FIXME[];
  handleSnackbar: (message: string, severity: AlertColor, action?: $FIXME) => void;
}

const SlackChannelCreateModal: React.FC<ISlackChannelCreateModalProps> = props => {
  const { isOpen, channelName, onClose, getMatchingChannels, handleSnackbar, communityOptions } =
    props;
  const [slackChannelName, setSlackChannelName] = useState<string>(channelName);
  const [tags, setTags] = useState<ITagJson[]>([]);
  const [requireAllTags, setRequireAllTags] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [communities, setCommunities] = useState<string[]>(
    _.compact([communityOptions[0]?.id.toString() ?? null]),
  );

  const onTagsEdited = (newTags: $FIXME) => {
    setTags(newTags);
  };

  const createChannel = async () => {
    try {
      if (!communities.length) {
        handleSnackbar(
          'You must select at least one community before creating the channel',
          'error',
        );
        return;
      }
      setLoading(true);
      await apiFetch(null, `corp-api/scalers/slack_channels/channel`, {
        method: 'POST',
        body: {
          channelName: slackChannelName,
          slackChannelConfig: tags.length
            ? {
                tags: tags.map((tag: { _id: string }) => tag._id) || [],
                requireAllTags,
              }
            : undefined,
          communities,
        },
      });
      handleSnackbar(
        'Slack Channel created successfully.',
        'success',
        <Button href="https://airtable.com/shrnOKrJwtB8sIlOb" target="_blank">
          Request approval
        </Button>,
      );
      setLoading(false);
      if (onClose) onClose();
    } catch (error) {
      handleSnackbar(error?.response?.error ?? error?.message ?? '', 'error');
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen}>
      <DialogTitle>
        <Box className="create-channel-dialog-title">
          <Typography.H5 className={'create-slack-channel-title'}>
            Create Slack Channel
          </Typography.H5>
          <Tooltip title="The channel/s to create should have a non-existing name and at least one tag">
            <IconButton>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box className={'create-slack-channel-dialog-content'}>
          <Box
            className={
              'slack-channel-name-and-communities-container scaleui scale-theme scale-tailwind'
            }
          >
            <TextField
              required={true}
              label="Slack Channel Name"
              placeholder="Channel name..."
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSlackChannelName(e.target.value);
              }}
              value={slackChannelName}
              className={'slack-channel-name'}
            />
            <SelectMenu
              selectedOptionId={communities}
              multiple
              options={communityOptions}
              setSelected={c => setCommunities(c)}
              label="Communities"
              menuDirection={'bottom'}
              renderValue={ids => {
                if (!ids.length) {
                  return 'None';
                }
                return ids.map(id => communityOptions.find(c => c.id === id)?.name).join(', ');
              }}
            />
          </Box>
          <Box className={'tags-main-container'}>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box className={'tags-container'}>
                <Typography.H5 className={twMerge('scaleui', 'create-slack-channel-title')}>
                  Tags
                </Typography.H5>
                <Tooltip title="The same rule will apply for the different workspaces">
                  <IconButton>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box className={'tags-and-checkbox-container'}>
                <Box style={{ width: '450px' }}>
                  <TagSelector tags={tags ?? []} onTagsChanged={newTags => onTagsEdited(newTags)} />
                </Box>
                <Checkbox
                  checked={requireAllTags}
                  onChange={() => {
                    setRequireAllTags(!requireAllTags);
                  }}
                  label="Require All Tags"
                  className={'checkbox-container'}
                ></Checkbox>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Tooltip
          title={
            !slackChannelName || getMatchingChannels(slackChannelName).length
              ? 'A channel with that name already exists'
              : ''
          }
        >
          <span>
            <Button
              type="primary"
              disabled={
                !!(!slackChannelName || getMatchingChannels(slackChannelName).length) ||
                loading ||
                !tags.length
              }
              onClick={() => createChannel()}
            >
              Create
            </Button>
          </span>
        </Tooltip>
        <Button
          type="error"
          onClick={() => {
            if (onClose) onClose();
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SlackChannels = () => {
  const [selectedChannel, setSelectedChannel] = useState<$FIXME>();
  const [channelMembers, setChannelMembers] = useState<$FIXME>([]);
  const [channelMembersCount, setChannelMembersCount] = useState();
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingSidebar, setLoadingSidebar] = useState(false);
  const [cursor, setCursor] = useState();
  const [page, setPage] = useState(0);
  const [inputError, setInputError] = useState<boolean>(false);

  const [isInviteModelOpen, setIsInviteModalOpen] = useState(false);
  const [slackChannelConfig, setSlackChannelConfig] = useState<$FIXME>({});
  const [slackChannelCompleteObject, setSlackChannelCompleteObject] = useState<$FIXME>({});

  const [slackChannels, setSlackChannels] = useState<$FIXME>();
  const [matchingSearchChannels, setMatchingSearchChannels] = useState<$FIXME>([]);
  const [searchedChannelName, setSearchedChannelName] = useState<string>('');
  const [isCreateChannelModalOpen, setIsCreateChannelModalOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    'children' | 'severity' | 'action'
  > | null>(null);

  const [searchValue, setSearchValue] = useState<string>('');
  const [isSearch, setIsSearch] = useState(false);

  const [selectedChannelPermissionGroups, setSelectedChannelPermissionGroups] = useState<$FIXME>(
    [],
  );

  const [selectedChannelCommunities, setSelectedChannelCommunities] = useState<ICommunity[]>([]);

  const [searchOption, setSearchOption] = useState<SearchOptions>(SearchOptions.Email);

  const { communities } = useGetCommunities();

  const onTagsEdited = (newTags: $FIXME) => {
    const updatedChannelConfig = _.cloneDeep(slackChannelConfig);
    updatedChannelConfig.tags = newTags;
    setSlackChannelConfig(updatedChannelConfig);
  };

  useEffect(() => {
    const getAllSlackChannels = async () => {
      setLoadingSidebar(true);

      const allSlackChannels = await apiFetch(null, `corp-api/scalers/slack_channels/channels`);
      setSlackChannels(_.sortBy(allSlackChannels, r => r.name));
      setLoadingSidebar(false);
    };

    if (!slackChannels) {
      getAllSlackChannels();
    }
  }, [slackChannels]);

  const resetSearch = async () => {
    setSearchValue('');
    setIsSearch(false);
    await getChannelMembers(selectedChannel);
    setPage(1);
  };

  const handleSnackbar = (children: string, severity: AlertColor, action?: $FIXME) =>
    setSnackbar({ children, severity, action });

  const createOrUpdateChannelConfig = async () => {
    const { conditionId = '', requireAllTags } = slackChannelConfig;
    try {
      const result = await apiFetch(
        null,
        `corp-api/scalers/slack_channels/${selectedChannel.channelId}/add_tag_condition`,
        {
          method: 'PUT',
          body: {
            tags: slackChannelConfig?.tags?.map((tag: { _id: string }) => tag?._id) || [],
            requireAllTags,
            conditionId,
          },
        },
      );
      setSlackChannelConfig({
        channelId: result?.channelId,
        tags: result?.tags,
        requireAllTags: result?.requireAllTags,
        conditionId: result?.conditionId,
      });
      setSlackChannelCompleteObject(result?.updatedCompleteChannel ?? {});
      handleSnackbar(
        `Slack channel config successfully ${conditionId ? 'updated' : 'created'}`,
        'success',
      );
    } catch (error) {
      handleSnackbar(error?.response?.error ?? error?.message ?? '', 'error');
    }
  };

  const getMatchingChannels = (searchChannelName: string) => {
    const matchingChannels = slackChannels.filter(
      (item: $FIXME) => item.name === searchChannelName || item.channelId === searchChannelName,
    );
    return matchingChannels;
  };

  const handleInputValidation = (value: string, option: SearchOptions) => {
    if (!value) {
      setInputError(false);
      return;
    }
    switch (option) {
      case SearchOptions.Email: {
        const emails = parseEmails(value || '');
        if (!emails?.length) {
          setInputError(true);
          return;
        }
        break;
      }
      case SearchOptions.SlackEmail: {
        const slackEmails = parseEmails(value || '');
        if (!slackEmails?.length) {
          setInputError(true);
          return;
        }
        break;
      }

      case SearchOptions.SlackId: {
        const slackIds = parseSlackIds(value || '');
        if (!slackIds?.length) {
          setInputError(true);
          return;
        }
        break;
      }
    }
    setInputError(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    let query = {};
    switch (searchOption) {
      case SearchOptions.Email:
        query = { emails: parseEmails(searchValue || '') };
        break;

      case SearchOptions.SlackEmail:
        query = { slackEmails: parseEmails(searchValue || '') };
        break;

      case SearchOptions.SlackId:
        query = { slackIds: parseSlackIds(searchValue || '') };
        break;
    }
    const result = await apiFetch(
      null,
      `corp-api/scalers/slack_channels/${selectedChannel.channelId}/members/search`,
      {
        query: {
          ...query,
          isExpert: selectedChannel?.isExpert,
        },
      },
    );

    setChannelMembersCount(result.count);
    setChannelMembers(result.members);
    setPage(1);
    setCursor(result.cursor);
    setLoading(false);
    setIsSearch(true);
  };

  const getChannelMembers = async (channel: $FIXME, switchChannel: boolean = false) => {
    setLoading(true);
    const channelMembersAPIUrl = `corp-api/scalers/slack_channels/${channel.channelId}/members`;
    const channelPermissionGroupsUrl = `corp-api/scalers/slack_channels/${channel.channelId}/permission_groups`;
    const channelConfigUrl = `corp-api/scalers/slack_channels/${channel.channelId}/config`;
    const communityChannelUrl = `corp-api/scalers/slack_channels/communities/channels/${channel.channelId}`;
    const channelObjectUrl = `corp-api/scalers/slack_channels/communities/channels/${channel.channelId}`;
    const query = { limit: TABLE_LIMIT, cursor, isExpert: channel.isExpert };
    if (cursor) {
      query.cursor = cursor;
    }

    const [
      membersResult,
      permissionGroupsResult,
      slackChannelConfigResult,
      communityChannelResult,
      slackChannelObjectResult,
    ] = await Promise.all([
      apiFetch(null, channelMembersAPIUrl, { query }),
      apiFetch(null, channelPermissionGroupsUrl, { query }),
      apiFetch(null, channelConfigUrl),
      apiFetch(null, communityChannelUrl),
      apiFetch(null, channelObjectUrl),
    ]);
    setChannelMembersCount(membersResult.count);
    setChannelMembers([...channelMembers, ...membersResult.members]);
    setSelectedChannelPermissionGroups(permissionGroupsResult);
    setSlackChannelConfig(slackChannelConfigResult);
    setSlackChannelCompleteObject(slackChannelObjectResult?.communityChannel ?? {});
    setSelectedChannelCommunities(communityChannelResult?.communities ?? []);
    setPage(switchChannel ? 1 : page + 1);
    setCursor(membersResult.cursor);
    setLoading(false);
    setLoadingTable(false);
  };

  const getPreviousPage = () => {
    setPage(page - 1);
  };

  const getNextPage = async () => {
    if (channelMembers.length <= page * TABLE_LIMIT) {
      await getChannelMembers(selectedChannel);
    } else {
      setPage(page + 1);
    }
  };

  const channelList = matchingSearchChannels.length ? matchingSearchChannels : slackChannels;

  const openSelectedIfUrl = ({ name, value }: OnSelectProps) => {
    if (name && value && typeof value === 'string' && name.toLowerCase().includes('url')) {
      try {
        const url = new URL(value);
        const newTab = window.open(url, '_blank', 'noopener,noreferrer');
        if (newTab) {
          newTab.opener = null;
        }
      } catch (err) {
        SAlert.error(err);
      }
    }
  };

  return (
    <div className="page-layout">
      <div className="channel-list-sidebar">
        <div style={{ color: 'white' }}>
          <h3 style={{ color: 'white', cursor: 'pointer' }} onClick={() => selectedChannel(null)}>
            Worker Slack Channels{' '}
          </h3>
          <div style={{ margin: '-12px 0px 12px' }}>
            {slackChannels && slackChannels.length ? `(${channelList.length} channels)` : ''}
          </div>
        </div>
        {loadingSidebar ? (
          <div style={{ position: 'absolute', left: '50%', top: '50%' }}>
            <CircularProgress />
          </div>
        ) : channelList ? (
          <>
            <div className="channel-search-container">
              <TextInput
                size="large"
                fullWidth
                placeholder="Channel Name/Id"
                label="Search Channel"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchedChannelName(e.target.value);
                  const search = new RegExp(e.target.value, 'i');
                  const matchingChannels = slackChannels.filter(
                    (item: $FIXME) => search.test(item.name) || search.test(item.channelId),
                  );
                  setMatchingSearchChannels(matchingChannels);
                }}
                InputLabelProps={{
                  style: { color: 'var(--color-RemoGray10)' },
                }}
              />
            </div>
            {channelList.map((channel: $FIXME) => (
              <div
                key={channel.channelId}
                className={`channel-list-item${
                  selectedChannel && selectedChannel.channelId === channel.channelId
                    ? '-selected'
                    : ''
                }`}
                onClick={() => {
                  setLoadingTable(true);
                  setSelectedChannel(channel);
                  getChannelMembers(channel, true);
                }}
              >
                {channel.isPrivate ? '🔒' : '#'}
                <strong>{channel.name}</strong> ({channel.memberCount})
              </div>
            ))}
          </>
        ) : (
          <></>
        )}
      </div>
      {loadingTable ? (
        <div className="members-loader-container">
          <CircularProgress />
        </div>
      ) : (
        <div className="members-table-container">
          {!!selectedChannel && !!channelMembers && (
            <div className="members-table-container">
              <div className="members-header-container">
                <div className="channel-info-container">
                  <h1>
                    {selectedChannel.isPrivate ? '🔒' : '#'}
                    {selectedChannel.name} <small>{selectedChannel.channelId}</small>
                  </h1>
                  <div className={'communities-container'}>
                    {selectedChannelCommunities.map(({ _id, name }) => {
                      return (
                        <Chip key={_id.toString()} size="small" label={name} color={'success'} />
                      );
                    })}
                  </div>
                </div>
                <div className="members-count-container">
                  <h2>{channelMembersCount} Members</h2>
                  <div className="hsp__1" />
                  <div className="invite-container" onClick={() => setIsInviteModalOpen(true)}>
                    + Invite whitelisted Taskers
                  </div>
                </div>
                <div className="emails-search-container">
                  <h4 style={{ whiteSpace: 'nowrap' }}>
                    Search By{' '}
                    <Select
                      value={searchOption}
                      onChange={e => {
                        setSearchOption(e.target.value as SearchOptions);
                        handleInputValidation(searchValue, e.target.value as SearchOptions);
                      }}
                    >
                      {Object.values(SearchOptions).map((value: SearchOptions) => (
                        <Option value={value} key={value}>
                          {value}
                        </Option>
                      ))}
                    </Select>
                    :
                  </h4>
                  <div className="hsp__1" />
                  <TextInput
                    size="large"
                    fullWidth
                    placeholder={`${searchOption}s (Delimit any way you want)`}
                    label={inputError ? `${searchOption}s input is not valid` : `${searchOption}s`}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchValue(e.target.value);
                      handleInputValidation(e.target.value, searchOption);
                    }}
                    value={searchValue}
                    error={inputError}
                  />
                  <div className="hsp__1" />
                  <Button
                    type="primary"
                    disabled={!searchValue || loading || inputError}
                    onClick={() => handleSearch()}
                  >
                    Search
                  </Button>
                  <div className="hsp__1" />
                  {isSearch && (
                    <Button type="warning" onClick={() => resetSearch()}>
                      Reset
                    </Button>
                  )}
                </div>
              </div>
              <div className="sp__1" />

              <div className="members-table-container-inner">
                <div className="members-table-container-outer">
                  <Table
                    pageSize={TABLE_LIMIT}
                    showPagination={false}
                    loading={loading}
                    data={channelMembers.slice(
                      (page - 1) * TABLE_LIMIT,
                      (page - 1) * TABLE_LIMIT + TABLE_LIMIT,
                    )}
                    columns={[
                      {
                        Header: '',
                        width: 60,
                        sortable: false,
                        // eslint-disable-next-line react/display-name
                        Cell: ({ original }) => {
                          return (
                            <>
                              <div className="profile-image-container">
                                <img src={original.profileImage} />
                              </div>
                              <style jsx>{`
                                .profile-image-container {
                                  height: 36px;
                                  width: 36px;
                                }
                                img {
                                  border-radius: 50%;
                                }
                              `}</style>
                            </>
                          );
                        },
                      },
                      {
                        Header: 'User ID',
                        accessor: '_id',
                        width: 230,
                        sortable: false,
                        // eslint-disable-next-line react/display-name
                        Cell: ({ original }) => (
                          <a href={`https://dashboard.scale.com/corp/lookup/${original._id}`}>
                            {original._id}
                          </a>
                        ),
                      },
                      {
                        Header: 'Slack ID',
                        accessor: 'workerSlackId',
                        width: 120,
                        sortable: false,
                      },
                      {
                        Header: 'Expert Slack ID',
                        accessor: 'expertSlackId',
                        width: 120,
                        sortable: false,
                      },
                      {
                        Header: 'Display Name',
                        accessor: 'displayName',
                        width: 270,
                        sortable: false,
                      },
                      { Header: 'Slack Name', accessor: 'slackName', width: 270, sortable: false },
                      { Header: 'Email', accessor: 'email', width: 270, sortable: false },
                      {
                        Header: 'Slack Email',
                        accessor: 'slackEmail',
                        width: 270,
                        sortable: false,
                      },
                      {
                        Header: 'Worker Source',
                        accessor: 'workerSource',
                        width: 170,
                        sortable: false,
                      },
                      {
                        Header: 'IP Country',
                        accessor: 'ipCountryCode',
                        width: 100,
                        sortable: false,
                      },
                      { Header: 'Country', accessor: 'countryCode', width: 100, sortable: false },
                      {
                        Header: 'Last Online Date',
                        accessor: 'lastOnlineDatetime',
                        width: 220,
                        sortable: false,
                      },
                    ]}
                  />
                  <div className="pagination-buttons-container">
                    <div className="pagination-button-container">
                      <Button disabled={page < 2 || loading} onClick={() => getPreviousPage()}>
                        Prev
                      </Button>
                    </div>
                    <div className="pagination-page-container">
                      {page} / {Math.ceil((channelMembersCount || 0) / TABLE_LIMIT)}
                    </div>
                    <div className="pagination-button-container">
                      <Button
                        onClick={() => getNextPage()}
                        disabled={
                          loading || page === Math.ceil((channelMembersCount || 0) / TABLE_LIMIT)
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="permission-groups-container">
                  {Object.keys(slackChannelCompleteObject).length && (
                    <div className="json-viewer-container">
                      <Typography.H5>Channel Conditions</Typography.H5>
                      <div className="json-viewer">
                        <ReactJsonView
                          src={{ conditions: slackChannelCompleteObject?.conditions }}
                          name={false}
                          theme="monokai"
                          iconStyle="triangle"
                          collapsed={2}
                          onSelect={openSelectedIfUrl}
                          collapseStringsAfterLength={120}
                          groupArraysAfterLength={false}
                          enableClipboard={true}
                          displayObjectSize={false}
                          displayDataTypes={false}
                          style={{ padding: '5px', borderRadius: '4px' }}
                        />
                      </div>
                    </div>
                  )}
                  {!selectedChannelPermissionGroups.length && (
                    <div className="permission-groups-container">
                      <Box className="create-channel-dialog-title">
                        <Typography.H5>Channel Configs</Typography.H5>
                        <Tooltip title="The config to create/update should have at least one tag">
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <div className="permission-group-container">
                        <h2>Tags: </h2>
                        <div style={{ minWidth: '200px', maxWidth: '600px' }}>
                          <TagSelector
                            tags={slackChannelConfig?.tags ?? []}
                            onTagsChanged={newTags => onTagsEdited(newTags)}
                          />
                        </div>
                        <div className="pagination-buttons-container">
                          <label>
                            <input
                              type="checkbox"
                              checked={slackChannelConfig?.requireAllTags}
                              onChange={() => {
                                const updatedChannelConfig = _.cloneDeep(slackChannelConfig);
                                updatedChannelConfig.requireAllTags =
                                  !slackChannelConfig?.requireAllTags;
                                setSlackChannelConfig(updatedChannelConfig);
                              }}
                            />
                            Require All Tags
                          </label>
                          <div className="hsp__1" />
                          <Button
                            type="primary"
                            onClick={() => {
                              createOrUpdateChannelConfig();
                            }}
                          >
                            {slackChannelConfig?.conditionId ? 'Save' : 'Create'}
                          </Button>
                          <div className="hsp__1" />
                        </div>
                      </div>
                    </div>
                  )}
                  {!!selectedChannelPermissionGroups.length && (
                    <div className="permission-groups-container">
                      <h2 className="permission-groups-title"> Channel Permission Groups</h2>
                      {selectedChannelPermissionGroups.map((pg: $FIXME) => {
                        return (
                          <div key={pg.id} className="permission-group-container">
                            <h3>{pg.name}</h3>
                            <div>
                              <a
                                href={`https://dashboard.scale.com/corp/lookup/${pg.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {pg.id}
                              </a>
                            </div>
                            <div className="sp__1" />
                            <SlackChannelCountries channel={pg.slackChannel} />
                            <div className="sp__1" />
                            <SlackChannelReviewLevels channel={pg.slackChannel} />
                            <div className="sp__1" />
                            <SlackChannelWorkerSources channel={pg.slackChannel} />
                            <div className="sp__1" />
                            <SlackChannelTierStatuses channel={pg.slackChannel} />
                            <div className="sp__1" />
                            <SlackChannelTags channel={pg.slackChannel} />
                            <div className="sp__1" />
                            <SlackChannelRequireAllTags channel={pg.slackChannel} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="sp__2" />
        </div>
      )}

      <SlackInviteTaskerModal
        isOpen={isInviteModelOpen}
        channelName={selectedChannel?.name ?? ''}
        channelId={selectedChannel?.channelId}
        onInvitePre={() => {
          setLoading(true);
          setIsInviteModalOpen(false);
        }}
        onInvitePost={(result: any) => {
          setChannelMembersCount(result.count);
          setChannelMembers(result.members);
          setPage(1);
          setCursor(result.cursor);
          setLoading(false);
        }}
        onClose={() => setIsInviteModalOpen(false)}
        isExpert={selectedChannel?.isExpert}
      />
      {isCreateChannelModalOpen && (
        <SlackChannelCreateModal
          isOpen={isCreateChannelModalOpen}
          channelName={searchedChannelName}
          onClose={() => setIsCreateChannelModalOpen(false)}
          getMatchingChannels={getMatchingChannels}
          handleSnackbar={handleSnackbar}
          communityOptions={communities.map(({ _id, name }) => {
            return {
              id: _id.toString(),
              name,
              label: name,
            };
          })}
        />
      )}

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={10000}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}

      <style>{`
        .info-box-container {
          display: flex;
          flex-direction: row;
        }

        .channel-info-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          gap: 4px,
        }

        .communities-container { 
          display: flex; 
          flex-direction: row; 
          gap: 4px; 
        }

        .slack-channel-name {
          width: 300px;
        }

        .create-slack-channel-title {
          margin: 0;
        }

        .tags-container {
          display: flex; 
          flex-direction: column;
          gap: 2px;
        }

        .create-slack-channel-dialog-content {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 64px;
          width: 800px;
        }

        .slack-channel-name-and-communities-container {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 8px;
        }

        .tags-main-container {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 4px;
        }

        .tags-container {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
        }

        .tags-and-checkbox-container {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }

        .checkbox-container {
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
        }

        .info-box {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
          border: 1px solid var(--color-RemoGray20);
          border-radius: 32px;
          width: 230px;
          margin: 24px;
        }
        .admins-table-container {
          display: flex;
          flex-direction: column;
        }
        .admin-table-container {
          width: 1020px;
          padding: 0px 24px;
        }
        .dashboard-container {
          padding: 0px 24px;
        }
        .MuiOutlinedInput-notchedOutline {
          border-color: var(--color-RemoGray20);
          border-radius: 12px;
        }
        .item-option-container {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        .permission-groups-title {
          padding: 0 8px;
        }
        .permission-group-container {
          padding: 1px 22px 14px 22px;
          border: 1px solid var(--color-RemoGray20);
          border-radius: 32px;
          margin-bottom: 14px;
        }
        .permission-groups-container {
          margin: 0px 32px 0px 32px;
          max-height: 1100px;
          width: 400px;
        }

        .reset-email-button {
          cursor: pointer;
          width: 50px;
        }
        .emails-search-container {
          display: flex;
          align-items: center;
          width: 520px;
        }
        .channel-search-container {
          padding: 6px 0px;
          margin-bottom: 16px;
        }
        .members-loader-container {
          position: absolute;
          top: 50%;
          left: 50%;
        }
        .members-table-container {
          padding: 16px;
        }
        .members-table-container {
          width: 130vw;
        }

        .members-table-container-inner {
          display: flex;
          flex-direction: row;
        }
        .members-table-container-outer {
          width: 900px;
        }
        .channel-list-sidebar {
          overflow: hidden;
          overflow-y: scroll;

          height: calc(100vh - 60px);
          width: 375px;
          min-width: 375px;
          max-width: 375px;
          background: #525252;
          color: white;
          display: flex;
          flex-direction: column;
          padding: 24px;
          position: -webkit-sticky;
          position: sticky;
          top: -32px;
        }
        .channel-list-item {
          padding: 6px 6px;
          cursor: pointer;
        }

        .channel-list-item-selected,
        .channel-list-item:hover {
          padding: 6px 6px;
          cursor: pointer;
          background-color: var(--color-RemoGray75);
          border-radius: 16px;
        }

        .page-layout {
          display: flex;
          flex-direction: row;
        }
        .members-header-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .members-count-container {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        .invite-container {
          padding: 6px 6px;
          font-size: 13px;
          font-weight: 500;
          color: var(--color-RemoBlue);
          display: inline-block;
        }

        .invite-container:hover {
          cursor: pointer;
          background-color: var(--color-RemoGray10);
          border-radius: 16px;
        }

        .pagination-page-container {
          padding: 0 6px;
        }
        .pagination-buttons-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding-top: 28px;
        }

        .pagination-button-container {
          padding: 0 4px;
        }

        .ReactTable .rt-thead.-header {
          background-color: #e5e5e5 !important;
          box-shadow: none !important;
          height: 40px !important;
          border-radius: 8px 8px 0 0;
        }

        .ReactTable {
          border: 1px solid rgba(0, 0, 0, 0.05);
        }

        .ReactTable .rt-thead.-header .rt-th {
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }

        .ReactTable .rt-thead .rt-th {
          border-right: none !important;
        }

        .ReactTable .rt-tr {
          padding: 8px 14px;
        }

        .ReactTable .rt-tbody {
          overflow: hidden;
        }

        .create-channel-dialog-title {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
        }
        .json-viewer {
          border-radius: 4px;
          padding: 3px;
        }
        .json-viewer-container {
          margin: 15px 32px 0px 32px;
          max-height: 1100px;
          width: 400px;
        }
      `}</style>
    </div>
  );
};

export default class SlackChannelsPage extends React.Component<$FIXME, $FIXME> {
  render() {
    return (
      <div className="layout">
        <SlackChannels />
        <style jsx>{`
          .layout {
            display: flex;
            margin: -32px;
          }
        `}</style>
      </div>
    );
  }
}
